"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader2, CheckCircle2, AlertCircle, Send } from "lucide-react";
export default function InvitacionAInscribirse() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="70280" id="llamado-a-la-acción-inscripción" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[128px] animate-pulse" style={{
        animationDuration: '4s'
      }} />
        <div className="absolute bottom-1/4 right-1/4 w-[30rem] h-[30rem] bg-secondary/15 rounded-full blur-[128px] animate-pulse" style={{
        animationDuration: '6s',
        animationDelay: '2s'
      }} />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9InJnYmEoMCwwLDAsMC4wNSkiLz48L3N2Zz4=')] opacity-30" />
      </div>

      <div ref={ref} className={`container relative z-10 mx-auto px-4 md:px-6 max-w-4xl transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 text-foreground">
            Comienza Tu Transformación Profesional
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Únete a nuestra comunidad educativa y asegura tu lugar en los cursos con mayor salida laboral. Capacítate con expertos y obtén certificaciones que impulsarán tu carrera.
          </p>
        </div>

        <div className="bg-card/60 backdrop-blur-xl border border-border/60 rounded-[var(--radius)] p-6 md:p-10 shadow-xl">
          <FormProvider formId="enrollment_invite_form_courses" sectionName="invitacion-a-inscribirse" defaultValues={{
          full_name: "",
          email_address: "",
          phone_number: "",
          organization: "",
          job_title: "",
          course_interest: "",
          additional_message: ""
        }} validationRules={{
          full_name: {
            required: "Este campo es obligatorio"
          },
          email_address: {
            required: "Este campo es obligatorio",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Por favor, introduce un correo electrónico válido"
            }
          },
          phone_number: {
            pattern: {
              value: /^[0-9+\-\s()]*$/,
              message: "Formato de teléfono inválido"
            }
          },
          course_interest: {
            required: "Por favor, selecciona un programa"
          }
        }} submitText="Inscribirme Ahora" successMessage="¡Inscripción recibida! Un asesor educativo se pondrá en contacto contigo por WhatsApp en las próximas 24 horas para finalizar el proceso.">
            <EnrollmentForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function EnrollmentForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-secondary/20 rounded-full flex items-center justify-center mb-6">
          <CheckCircle2 className="w-8 h-8 text-secondary" />
        </div>
        <h3 className="text-2xl font-semibold mb-2 text-foreground">¡Solicitud Enviada!</h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={() => window.open('https://wa.me/59896344395', '_blank')} variant="outline" className="min-w-[200px] rounded-full">
          Inscribir a otra persona
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Nombre y Apellido <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input placeholder="Ej: Juan García" className="rounded-xl border-input/60 bg-background/50" {...field} />
              </FormControl>
              <FormDescription>Ingresa tu nombre tal como figurará en tu certificado.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email_address" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Correo Electrónico <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="juan.garcia@email.com" className="rounded-xl border-input/60 bg-background/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">WhatsApp / Teléfono</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+54 9 11 1234 5678" className="rounded-xl border-input/60 bg-background/50" {...field} />
              </FormControl>
              <FormDescription>Indica tu número con código de área para recibir el programa.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="organization" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Localidad / Ciudad</FormLabel>
              <FormControl>
                <Input placeholder="Ej: Posadas, Misiones" className="rounded-xl border-input/60 bg-background/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="job_title" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Nivel de Estudios</FormLabel>
              <FormControl>
                <Input placeholder="Ej: Secundario Completo" className="rounded-xl border-input/60 bg-background/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="course_interest" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Programa de Interés <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <select className="flex h-10 w-full items-center justify-between rounded-xl border border-input/60 bg-background/50 px-3 py-1 text-base shadow-sm transition-[color,box-shadow] outline-none focus-visible:border-primary focus-visible:ring-primary/20 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm appearance-none" {...field}>
                    <option value="" disabled>Selecciona un programa</option>
                    {["Técnico Electricista", "Auxiliar Administrativo Contable", "Barbería Profesional", "Kinesiología Deportiva", "Técnico en Aires Acondicionados", "Otro Programa"].map((option, i) => <option key={i} value={option} data-index={i} className="bg-background text-foreground">
                        {option}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="additional_message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Consulta Adicional (opcional)</FormLabel>
            <FormControl>
              <textarea className="flex min-h-[100px] w-full rounded-xl border border-input/60 bg-background/50 px-3 py-2 text-base shadow-sm transition-[color,box-shadow] outline-none focus-visible:border-primary focus-visible:ring-primary/20 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-y" placeholder="¿Tienes alguna duda sobre horarios o formas de pago?" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive-foreground">{submitError}</p>
        </div>}

      <div className="pt-4 flex justify-end">
        <Button type="submit" size="lg" className="w-full md:w-auto min-w-[220px] rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg transition-all">
          Inscribirme Ahora
          <Send className="ml-2 size-4" />
        </Button>
      </div>
    </Form>;
}